import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type { HostedAgentInvoiceLine } from "@/api/admin/hosted-agents-usage"

import { downloadHostedAgentInvoiceCsv } from "@/api/admin/hosted-agents-usage"
import { Download } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

import { useAdminHostedAgentInvoiceQuery } from "../hooks/queries/hosted-agents"
import {
  invoiceCsvFilename,
  recentInvoiceMonths,
  triggerBlobDownload,
} from "../utils/hosted-agent-invoice-utils"

/**
 * Invoice basis per hosted agent for one calendar month (#3039): billable runs
 * by initiator and outcome, the heavy/light split, tokens and SEK — the
 * numbers finance bills from, with a CSV that opens in Swedish Excel as-is.
 */
export function HostedAgentInvoiceCard() {
  const { t, i18n } = useTranslation()
  const months = useMemo(() => recentInvoiceMonths(new Date()), [])
  const [month, setMonth] = useState(months[0])
  const [downloading, setDownloading] = useState(false)
  const invoiceQuery = useAdminHostedAgentInvoiceQuery(month)
  const invoice = invoiceQuery.data
  const locale = i18n.resolvedLanguage ?? "sv"
  const sek = (value: number) =>
    new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "SEK",
      maximumFractionDigits: 2,
    }).format(value)
  const num = (value: number) => new Intl.NumberFormat(locale).format(value)
  const monthLabel = (value: string) =>
    new Intl.DateTimeFormat(locale, { month: "long", year: "numeric", timeZone: "UTC" }).format(
      new Date(`${value}-01T00:00:00Z`)
    )

  const handleDownload = async () => {
    setDownloading(true)
    try {
      triggerBlobDownload(await downloadHostedAgentInvoiceCsv(month), invoiceCsvFilename(month))
    } finally {
      setDownloading(false)
    }
  }

  const renderBody = () => {
    if (invoiceQuery.isPending) {
      return (
        <div className="space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      )
    }
    if (invoiceQuery.isError) {
      return (
        <p className="text-muted-foreground text-sm" role="alert">
          {t("adminDashboard.hostedAgents.invoice.error")}
        </p>
      )
    }
    if (!invoice || invoice.lines.length === 0) {
      return (
        <p className="text-muted-foreground text-sm">
          {t("adminDashboard.hostedAgents.invoice.empty")}
        </p>
      )
    }
    return (
      <div className="overflow-x-auto">
        <Table className="tabular-nums">
          <TableHeader>
            <TableRow>
              <TableHead>{t("adminDashboard.hostedAgents.invoice.columns.agent")}</TableHead>
              <TableHead className="text-right">
                {t("adminDashboard.hostedAgents.invoice.columns.runs")}
              </TableHead>
              <TableHead className="text-right">
                {t("adminDashboard.hostedAgents.invoice.columns.heavyLight")}
              </TableHead>
              <TableHead className="text-right">
                {t("adminDashboard.hostedAgents.invoice.columns.failed")}
              </TableHead>
              <TableHead className="text-right">
                {t("adminDashboard.hostedAgents.invoice.columns.tokens")}
              </TableHead>
              <TableHead className="text-right">
                {t("adminDashboard.hostedAgents.invoice.columns.cost")}
              </TableHead>
              <TableHead className="text-right">
                {t("adminDashboard.hostedAgents.invoice.columns.perRun")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoice.lines.map((line: HostedAgentInvoiceLine) => (
              <TableRow key={line.agentId} data-testid="hosted-agent-invoice-line">
                <TableCell>
                  <div className="font-medium">{line.name ?? line.agentId}</div>
                  {line.slug ? (
                    <div className="text-muted-foreground text-xs">{line.slug}</div>
                  ) : null}
                </TableCell>
                <TableCell className="text-right">
                  {num(line.runs)}
                  {line.routineRuns > 0 ? (
                    <span className="text-muted-foreground text-xs">
                      {" "}
                      (
                      {t("adminDashboard.hostedAgents.invoice.routineShort", {
                        count: line.routineRuns,
                      })}
                      )
                    </span>
                  ) : null}
                </TableCell>
                <TableCell className="text-right">
                  {num(line.heavyRuns)} / {num(line.lightRuns)}
                </TableCell>
                <TableCell className="text-right">{num(line.failedRuns)}</TableCell>
                <TableCell className="text-right">{num(line.totalTokens)}</TableCell>
                <TableCell className="text-right font-medium">{sek(line.costSek)}</TableCell>
                <TableCell className="text-muted-foreground text-right text-xs">
                  {sek(line.costPerRunP50Sek)} / {sek(line.costPerRunMaxSek)}
                </TableCell>
              </TableRow>
            ))}
            <TableRow className="font-semibold" data-testid="hosted-agent-invoice-totals">
              <TableCell>{t("adminDashboard.hostedAgents.invoice.total")}</TableCell>
              <TableCell className="text-right">{num(invoice.totals.runs)}</TableCell>
              <TableCell className="text-right">
                {num(invoice.totals.heavyRuns)} / {num(invoice.totals.lightRuns)}
              </TableCell>
              <TableCell className="text-right">{num(invoice.totals.failedRuns)}</TableCell>
              <TableCell className="text-right">{num(invoice.totals.totalTokens)}</TableCell>
              <TableCell className="text-right">{sek(invoice.totals.costSek)}</TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
        <p className="text-muted-foreground mt-3 text-xs">
          {t("adminDashboard.hostedAgents.invoice.footnote", {
            heavy: invoice.heavyRunSek,
            light: invoice.lightRunSek,
            excluded: invoice.totals.excludedRuns,
          })}
        </p>
      </div>
    )
  }

  return (
    <Card data-testid="hosted-agent-invoice-card">
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <CardTitle>{t("adminDashboard.hostedAgents.invoice.title")}</CardTitle>
          <CardDescription>{t("adminDashboard.hostedAgents.invoice.description")}</CardDescription>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger
              aria-label={t("adminDashboard.hostedAgents.invoice.month")}
              className="w-44"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {months.map((value) => (
                <SelectItem key={value} value={value}>
                  {monthLabel(value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            disabled={downloading || !invoice}
            size="sm"
            variant="outline"
            onClick={() => void handleDownload()}
          >
            <Download aria-hidden="true" className="size-4" />
            {t("adminDashboard.hostedAgents.invoice.downloadCsv")}
          </Button>
        </div>
      </CardHeader>
      <CardContent>{renderBody()}</CardContent>
    </Card>
  )
}
